import React, { useEffect } from "react";
import axios from "axios";
import { Button, Dropdown, Menu, Message } from "@arco-design/web-react";
import { IconLeft, IconMoreVertical } from "@arco-design/web-react/icon";
import { GroupChannel } from '@sendbird/uikit-react/GroupChannel';
import ChannelSettings from '@sendbird/uikit-react/ChannelSettings';
import { useSendbirdStateContext } from "@sendbird/uikit-react";
import {
  GroupChannel as GroupChannelType,
  Member,
} from "@sendbird/chat/lib/__definition";
import { ReportReasonId } from "../../../../constants/reportReasons";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { ConversationAvatar } from "../ConversationAvatar/ConversationAvatar";
import { ConversationReportDialog, ModerationAction } from "../ConversationReportDialog/ConversationReportDialog";
import { AddMembersDialog } from "../AddMembersDialog/AddMembersDialog";
import { ManageGroupAdminsDialog } from "../ManageGroupAdminsDialog/ManageGroupAdminsDialog";
import { ViewGroupMembersDialog } from "../ViewGroupMembersDialog/ViewGroupMembersDialog";
import { GroupChannelSettingsDialog } from "../GroupChannelSettingsDialog/GroupChannelSettingsDialog";
import { getIfOperator } from "../../utils/getIfOperator";
import { useBreakpoint } from "../../../../utils/hooks/useBreakpoint";
import { getTextColor } from "../../../../utils/cssVariables";
import { useConversationRespondent } from "../../utils/useConversationRespondent";
import { endpoints } from "../../../../api/endpoints";
import { SYSTEM_MODERATOR_USER_ID } from "../../constants";
import { ensureGroupChatManagerAccess } from "../../utils/platformUserSearch";
import { isGroupChatChannel } from "../../utils/isGroupChatChannel";
import { RootState } from "../../../../store/store";

import './Conversation.css';


type Props = {
  activeConversation: GroupChannelType | null;
  onCloseConversation: () => void;
  onChannelUpdated?: (channel: GroupChannelType) => void;
}

export function Conversation({ activeConversation, onCloseConversation, onChannelUpdated }: Props) {
  const { t } = useTranslation();
  const { stores: { sdkStore: { sdk }, userStore } } = useSendbirdStateContext();
  const platformUser = useSelector((state: RootState) => state.user.user);

  const { isMobile } = useBreakpoint('mobile');
  const queryClient = useQueryClient();

  const [isModerationDialogOpened, setIsModerationDialogOpened] = React.useState<boolean>(false);
  const [isSettingsOpened, setIsSettingsOpened] = React.useState<boolean>(false);
  const [isGroupSettingsOpened, setIsGroupSettingsOpened] = React.useState<boolean>(false);
  const [isAddMembersOpened, setIsAddMembersOpened] = React.useState<boolean>(false);
  const [isManageAdminsOpened, setIsManageAdminsOpened] = React.useState<boolean>(false);
  const [isViewMembersOpened, setIsViewMembersOpened] = React.useState<boolean>(false);
  const [channelMembers, setChannelMembers] = React.useState<Member[]>([]);
  const [canManageGroupChat, setCanManageGroupChat] = React.useState<boolean>(false);
  const [channelName, setChannelName] = React.useState<string>("");

  const isOperator = activeConversation && getIfOperator(activeConversation, userStore.user);
  const isGroupChat = isGroupChatChannel(activeConversation);
  const canManageChat = isGroupChat ? canManageGroupChat : Boolean(isOperator);
  const {
    respondent,
    displayName: respondentDisplayName,
    avatarUrl: respondentAvatarUrl,
  } = useConversationRespondent(activeConversation, Boolean(isGroupChat));

  useEffect(() => {
    setChannelMembers(activeConversation?.members ?? []);
  }, [activeConversation]);

  useEffect(() => {
    setChannelName(activeConversation?.name ?? "");
  }, [activeConversation?.url, activeConversation?.name]);

  useEffect(() => {
    const ensureManagerAccess = async () => {
      if (!activeConversation?.url || !isGroupChat) {
        setCanManageGroupChat(false);
        return;
      }

      if (isOperator) {
        setCanManageGroupChat(true);
        return;
      }

      if (!platformUser?.isAdmin) {
        setCanManageGroupChat(false);
        return;
      }

      try {
        await ensureGroupChatManagerAccess(activeConversation.url);
        const channel = await sdk.groupChannel.getChannel(activeConversation.url);
        setCanManageGroupChat(getIfOperator(channel, userStore.user));
        setChannelMembers(channel.members ?? []);
      } catch {
        setCanManageGroupChat(false);
      }
    };

    ensureManagerAccess().catch(console.error);
  }, [activeConversation?.url, isGroupChat, isOperator, platformUser?.isAdmin, sdk.groupChannel, userStore.user]);

  useEffect(() => {
    setIsSettingsOpened(false);
    setIsGroupSettingsOpened(false);
    setIsAddMembersOpened(false);
    setIsManageAdminsOpened(false);
    setIsViewMembersOpened(false);
  }, [activeConversation])

  const moderationPayload = (message: string, category: ReportReasonId) => ({
    category: t(`messages.report_reasons.${category}`),
    details: message,
  });

  const refreshActiveChannel = async () => {
    if (!activeConversation?.url) {
      return;
    }
    try {
      const channel = await sdk.groupChannel.getChannel(activeConversation.url);
      setChannelMembers(channel.members ?? []);
      setChannelName(channel.name ?? "");
      onChannelUpdated?.(channel);
    } catch {
      // Keep existing channel state on refresh failure.
    }
  };

  const refreshChannelMembers = async () => {
    await refreshActiveChannel();
  };

  const handleBlockUser = async (message: string, category: ReportReasonId) => {
    if (!activeConversation || !respondent) {
      return;
    }
    try {
      await axios.post(endpoints.userModeration.block(respondent.userId), moderationPayload(message, category));
      const channel = await sdk.groupChannel.getChannel(activeConversation.url);
      await channel.leave();
      onCloseConversation();
      Message.success({ content: t("messages.block_user_success"), closable: true, duration: 3000 });
    } catch {
      Message.error({ content: t("messages.block_user_error"), closable: true, duration: 4000 });
    }
  };

  const handleReportUser = async (message: string, category: ReportReasonId) => {
    if (!activeConversation || !respondent) {
      return;
    }
    try {
      await axios.post(endpoints.userModeration.report(respondent.userId), moderationPayload(message, category));
      Message.success({ content: t("messages.report_user_success"), closable: true, duration: 3000 });
    } catch {
      Message.error({ content: t("messages.report_user_error"), closable: true, duration: 4000 });
    }
  };

  const handleModerationSubmit = async (action: ModerationAction, message: string, category: ReportReasonId) => {
    if (action === 'block') {
      await handleBlockUser(message, category);
    } else {
      await handleReportUser(message, category);
    }
  };

  const handleInviteModerator = () => {
    if (activeConversation) {
      sdk.groupChannel.getChannel(activeConversation.url).then((channel: GroupChannelType) => {
        channel.inviteWithUserIds([SYSTEM_MODERATOR_USER_ID]).then(() => {
          channel.sendUserMessage({
            message: t('messages.invite_moderator_message'),
            mentionedUserIds: [SYSTEM_MODERATOR_USER_ID]
          })
        });
      });
    }
  }

  const handleLeaveChannel = () => {
    if (!activeConversation?.url) {
      return;
    }
    sdk.groupChannel.getChannel(activeConversation.url).then((channel: GroupChannelType) => {
      channel.leave().then();
      onCloseConversation();
      queryClient.invalidateQueries({ queryKey: ['groupChannelCount'] })
    });
  }

  const handleOpenModerationDialog = () => {
    setIsModerationDialogOpened(true);
  }

  const handleShowSettings = () => {
    if (isGroupChat) {
      setIsGroupSettingsOpened(true);
      return;
    }
    setIsSettingsOpened(true);
  }

  const userMenuDropList = (
    <Menu>
      <Menu.Item key='1' onClick={handleOpenModerationDialog}>{t('messages.block_report_button')}</Menu.Item>
      <Menu.Item key='3' style={{ display: 'none' }} onClick={handleInviteModerator}>{t('messages.invite_moderator_button')}</Menu.Item>
    </Menu>
  );

  const groupChatMemberMenuDropList = (
    <Menu>
      <Menu.Item key='1' onClick={() => setIsViewMembersOpened(true)}>{t('messages.view_members_button')}</Menu.Item>
      <Menu.Item key='2' onClick={handleLeaveChannel}>{t('messages.leave_chat_button')}</Menu.Item>
    </Menu>
  );

  const groupChatOperatorMenuDropList = (
    <Menu>
      <Menu.Item key='1' onClick={() => setIsViewMembersOpened(true)}>{t('messages.view_members_button')}</Menu.Item>
      <Menu.Item key='2' onClick={() => setIsAddMembersOpened(true)}>{t('messages.add_members_button')}</Menu.Item>
      <Menu.Item key='3' onClick={() => setIsManageAdminsOpened(true)}>{t('messages.manage_admins_button')}</Menu.Item>
      <Menu.Item key='4' onClick={handleShowSettings}>{t('messages.group_settings_button')}</Menu.Item>
      <Menu.Item key='5' onClick={handleLeaveChannel}>{t('messages.leave_chat_button')}</Menu.Item>
    </Menu>
  );

  const directMessageOperatorMenuDropList = (
    <Menu>
      <Menu.Item key='4' onClick={handleShowSettings}>{t('messages.show_settings_button')}</Menu.Item>
    </Menu>
  );

  const generalMenu = isGroupChat ? groupChatMemberMenuDropList : userMenuDropList;
  const dropList = canManageChat
    ? (isGroupChat ? groupChatOperatorMenuDropList : directMessageOperatorMenuDropList)
    : generalMenu;

  const existingMemberIds = channelMembers.map((member) => member.userId);

  return (
    <>
      <ConversationReportDialog
        onSubmit={handleModerationSubmit}
        visible={isModerationDialogOpened}
        onClose={() => setIsModerationDialogOpened(false)}
      />
      {activeConversation?.url && (
        <>
          <AddMembersDialog
            visible={isAddMembersOpened}
            channelUrl={activeConversation.url}
            existingMemberIds={existingMemberIds}
            onClose={() => setIsAddMembersOpened(false)}
            onMembersAdded={() => {
              refreshChannelMembers().catch(console.error);
              Message.success({
                content: t('messages.add_members_success'),
                closable: true,
                duration: 3000,
              });
            }}
          />
          <ManageGroupAdminsDialog
            visible={isManageAdminsOpened}
            channelUrl={activeConversation.url}
            onClose={() => setIsManageAdminsOpened(false)}
            onUpdated={() => {
              refreshChannelMembers().catch(console.error);
            }}
          />
          {isGroupChat && (
            <ViewGroupMembersDialog
              visible={isViewMembersOpened}
              channelUrl={activeConversation.url}
              canManageChat={canManageChat}
              onClose={() => setIsViewMembersOpened(false)}
              onUpdated={() => {
                refreshChannelMembers().catch(console.error);
              }}
            />
          )}
          {isGroupChat && (
            <GroupChannelSettingsDialog
              visible={isGroupSettingsOpened}
              channelUrl={activeConversation.url}
              onClose={() => setIsGroupSettingsOpened(false)}
              onUpdated={() => {
                refreshChannelMembers().catch(console.error);
              }}
            />
          )}
        </>
      )}
      {
        isSettingsOpened && !isGroupChat ? (
          <div className="conversation__channel-settings">
            <ChannelSettings
              channelUrl={activeConversation?.url || ''}
              onCloseClick={() => setIsSettingsOpened(false)}
              disableUserProfile
            />
          </div>
        ) : (
          <GroupChannel
            channelUrl={activeConversation?.url || ''}
            disableUserProfile
            isReactionEnabled={false}
            renderChannelHeader={() => {
              return (
                <div className="conversation__header">
                  {isMobile && (
                    <Button onClick={onCloseConversation} className="conversation__header-button" type='text'>
                      <IconLeft fontSize={24} style={{ color: getTextColor() }}/>
                    </Button>
                  )}
                  <div className="conversation__info">
                    <ConversationAvatar
                      groupName={isGroupChat ? channelName : undefined}
                      user={respondent}
                      size={32}
                      displayName={respondentDisplayName || undefined}
                      avatarUrlOverride={respondentAvatarUrl || undefined}
                    />
                    <div>{canManageChat || isGroupChat ? channelName : (respondentDisplayName || respondent?.nickname)}</div>
                  </div>
                  <Dropdown droplist={dropList} trigger='click' position='br'>
                    <Button className="conversation__header-button" type='text'>
                      <IconMoreVertical fontSize={24} style={{ color: getTextColor() }}/>
                    </Button>
                  </Dropdown>
                </div>
              );
            }}
          />
        )
      }
    </>
  );
}
